import { __awaiter } from '../node_modules/tslib/tslib.es6.js';
import { fromCognitoIdentityPool } from '@aws-sdk/credential-providers';
import { Logger, Auth, Hub } from 'aws-amplify';
import { tryGetVisitorId } from '../analytics/fingerprint.js';
import '../analytics/events.js';
import 'uuid';
import 'lodash/includes';
import '../types/filters.js';
import '../utils/assetUtils.js';
import BrandclubStoreConfigUtils from '../utils/brandclubStoreConfigUtils.js';
import 'numeral';
import '../utils/stringUtils.js';
import '../utils/broadcast.js';
import '../utils/constants/Constants.js';
import 'awesome-phonenumber';
import 'react';
import 'lodash/isEqual';
import 'lodash/get';
import 'lodash/orderBy';
import '@splitsoftware/splitio-react';
import '@tanstack/react-query';
import '../types/Campaign.js';
import '../types/product.js';
import '../api/graphQl/brand.js';
import '../api/graphQl/product.js';
import '../api/cache/appConfigQueryOptions.js';
import '@brandclub/types';
import 'lodash/omitBy';
import 'lodash/isNil';
import { Logger as Logger$1 } from '../utils/logger.js';
import CrossDomainStorage from './CrossDomainStorage.js';
export { default as CrossDomainStorage } from './CrossDomainStorage.js';

const AmplifyAuth = Auth;
const AmplifyHub = Hub;
Logger.LOG_LEVEL = "NONE";
const logger = new Logger$1('Auth', BrandclubStoreConfigUtils.getAppStage());
const sharedVisitorIdCookieName = BrandclubStoreConfigUtils.getAppStage() === "prod"
    ? "bcVisitorId"
    : `bcVisitorId-${BrandclubStoreConfigUtils.getAppStage()}`;
const authConfiguredPromise = new Promise((resolve) => {
    const unsubscribe = AmplifyHub.listen("auth", (data) => {
        var _a;
        if (((_a = data === null || data === void 0 ? void 0 : data.payload) === null || _a === void 0 ? void 0 : _a.event) === "configured") {
            unsubscribe();
            resolve();
        }
    });
});
const waitForAuthConfigured = (timeout = 5000) => __awaiter(void 0, void 0, void 0, function* () {
    const timeoutPromise = new Promise((_, reject) => {
        logger.debug(`Waiting for auth to be configured with timeout of ${timeout}ms`);
        setTimeout(() => {
            reject(new Error("Auth not configured after timeout"));
        }, timeout);
    });
    return Promise.race([authConfiguredPromise, timeoutPromise]);
});
const getAWSCognitoConfig = (authConfig, domainName) => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const appStage = BrandclubStoreConfigUtils.getAppStage();
        const authDomain = BrandclubStoreConfigUtils.getLocalStorageDomain(appStage, {
            domainName,
        });
        // update current hostname to auth domain
        const authOrigin = `https://${authDomain}`;
        if (domainName) {
            logger.debug(`Using auth origin from provided domainName: ${domainName}`);
        }
        else {
            logger.warn(`Using fallback localStorageDomain: ${authOrigin} as domainName is not provided`);
        }
        // Create and sync the cross-domain storage
        const storage = CrossDomainStorage.createInstance(authOrigin, appStage);
        yield storage.initialize();
        let cognitoCredentials = undefined;
        return {
            region: authConfig.region,
            userPoolId: authConfig.userPoolId,
            userPoolWebClientId: authConfig.userPoolClientId,
            oauth: {
                domain: authConfig.signInDomain,
                scope: [
                    "phone",
                    "email",
                    "profile",
                    "openid",
                    "aws.cognito.signin.user.admin",
                ],
                redirectSignIn: `${origin}/signinredirect`,
                redirectSignOut: `${origin}/signoutredirect`,
                responseType: "code",
            },
            storage: storage,
            Auth: {
                domain: authDomain,
                userPoolId: authConfig.userPoolId,
                clientId: authConfig.userPoolClientId,
                identityPoolId: authConfig.identityPoolId,
                userPoolWebClientId: authConfig.userPoolClientId,
                ClientId: authConfig.userPoolClientId,
                region: authConfig.region,
                credentials: (() => {
                    if (!authConfig.identityPoolId) {
                        return undefined;
                    }
                    if (!cognitoCredentials) {
                        cognitoCredentials = fromCognitoIdentityPool({
                            identityPoolId: authConfig.identityPoolId,
                            clientConfig: { region: authConfig.region },
                        });
                    }
                    return cognitoCredentials;
                })(),
            },
        };
    }
    catch (err) {
        logger.error("Error getting AWS Cognito config", err);
        throw err;
    }
});
// Add function to redeem login code
const redeemBrandAppLoginCode = ({ apiDomain, loginCode, signal, }) => __awaiter(void 0, void 0, void 0, function* () {
    if (!apiDomain) {
        apiDomain = BrandclubStoreConfigUtils.getApiHostName();
    }
    else {
        apiDomain = "https://" + apiDomain;
    }
    const res = yield fetch(`${apiDomain}/api-rewards/brandapp/redeemBrandAppLoginCode`, {
        method: "POST",
        headers: {
            "Content-Type": "application/json",
        },
        body: JSON.stringify({ loginCode }),
        signal,
    });
    if (res.ok) {
        return res.json();
    }
    else {
        throw new Error("Error redeeming brand app login code");
    }
});
// Add function to check and handle login code
const checkAndHandleLoginCode = (apiDomain) => __awaiter(void 0, void 0, void 0, function* () {
    var _a, _b;
    const url = new URL(window.location.href);
    const loginCode = url.searchParams.get("bcLoginCode");
    const shouldLogOut = url.searchParams.get("bcLogout") === "1";
    if (!loginCode && !shouldLogOut) {
        return false;
    }
    // delete from the url without reloading the page
    url.searchParams.delete("bcLoginCode");
    url.searchParams.delete("bcLogout");
    window.history.replaceState({}, document.title, url.toString());
    try {
        if (loginCode && !shouldLogOut) {
            logger.debug(`Found login code in URL, attempting to redeem ${loginCode} from ${apiDomain}`);
            const res = yield redeemBrandAppLoginCode({ apiDomain, loginCode });
            if (res.status === "success" &&
                ((_a = res.data) === null || _a === void 0 ? void 0 : _a.accessToken) &&
                ((_b = res.data) === null || _b === void 0 ? void 0 : _b.refreshToken)) {
                logger.debug("Login code redeemed successfully");
                // We need to store tokens in the format that Amplify Auth expects
                // Amplify uses keys like: CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}
                // However, we don't have the username at this point, so we need to decode the token
                // Parse the access token to get user info
                const tokenParts = res.data.accessToken.split('.');
                if (tokenParts.length === 3) {
                    try {
                        const payload = JSON.parse(atob(tokenParts[1]));
                        const username = payload.username || payload['cognito:username'] || payload.sub;
                        // Get the auth config to get the client ID
                        const appStage = BrandclubStoreConfigUtils.getAppStage();
                        const storage = CrossDomainStorage.getCurrentInstance();
                        if (!storage) {
                            logger.error("CrossDomainStorage not initialized when trying to store login tokens");
                            return false;
                        }
                        if (username) {
                            // We need to wait for Auth to be configured to get the client ID
                            // For now, we'll store the tokens in a temporary location and let Auth pick them up
                            const tempAuthData = {
                                accessToken: res.data.accessToken,
                                accessTokenExpiresIn: res.data.accessTokenExpiresIn,
                                refreshToken: res.data.refreshToken,
                                refreshTokenExpiresIn: res.data.refreshTokenExpiresIn,
                                idToken: res.data.idToken,
                                idTokenExpiresIn: res.data.idTokenExpiresIn,
                                username: username,
                                timestamp: Date.now()
                            };
                            // Store in a temporary key that we'll process after Auth is initialized
                            storage.setItem(`bc-pending-auth-${appStage}`, JSON.stringify(tempAuthData));
                            return true;
                        }
                    }
                    catch (error) {
                        logger.error("Error parsing access token:", error);
                    }
                }
            }
        }
        else if (shouldLogOut) {
            try {
                logger.debug("Found bcLogout in URL, attempting to log out");
                yield signOut();
                return true;
            }
            catch (error) {
                logger.error("Error signing out:", error);
                return true;
            }
            finally {
                url.searchParams.delete("bcLogout");
                window.history.replaceState({}, document.title, url.toString());
            }
        }
    }
    catch (error) {
        logger.error("Error redeeming login code:", error);
    }
    return false;
});
const initializeAuth = (authConfig, domainName) => __awaiter(void 0, void 0, void 0, function* () {
    // First, initialize the storage bridge
    const cognitoConfig = yield getAWSCognitoConfig(authConfig, domainName);
    Auth.configure(cognitoConfig);
    const hasLoginCode = yield checkAndHandleLoginCode(domainName);
    const appStage = BrandclubStoreConfigUtils.getAppStage();
    const storage = CrossDomainStorage.getCurrentInstance();
    if (storage) {
        const pendingAuthKey = `bc-pending-auth-${appStage}`;
        const pendingAuthData = storage.getItem(pendingAuthKey);
        if (pendingAuthData) {
            try {
                const authData = JSON.parse(pendingAuthData);
                const clientId = authConfig.userPoolClientId;
                const username = authData.username;
                // Store tokens in Amplify's expected format
                // Format: CognitoIdentityServiceProvider.{clientId}.{username}.{tokenType}
                const keyPrefix = `CognitoIdentityServiceProvider.${clientId}.${username}`;
                // Store access token
                storage.setItem(`${keyPrefix}.accessToken`, authData.accessToken);
                // Store refresh token
                storage.setItem(`${keyPrefix}.refreshToken`, authData.refreshToken);
                // Store ID token
                storage.setItem(`${keyPrefix}.idToken`, authData.idToken);
                // Store the last authenticated user
                storage.setItem(`CognitoIdentityServiceProvider.${clientId}.LastAuthUser`, username);
                // Calculate and store token expiration time
                const clockDrift = 0; // You might want to calculate this based on server time
                storage.setItem(`${keyPrefix}.clockDrift`, clockDrift.toString());
                // Store user attributes (decode from access token)
                const tokenParts = authData.accessToken.split('.');
                if (tokenParts.length === 3) {
                    const payload = JSON.parse(atob(tokenParts[1]));
                    const userData = {
                        UserAttributes: [
                            { Name: 'sub', Value: payload.sub || username },
                        ],
                        Username: username
                    };
                    for (const key in payload) {
                        if (key !== "sub" && key !== "cognito:username") {
                            userData.UserAttributes.push({ Name: key, Value: payload[key] });
                        }
                    }
                    storage.setItem(`${keyPrefix}.userData`, JSON.stringify(userData));
                }
                // Remove the pending auth data
                storage.removeItem(pendingAuthKey);
                logger.debug("Successfully processed pending auth tokens");
                // Dispatch auth event to notify the app that user is signed in
                Hub.dispatch("auth", {
                    event: "signIn",
                    data: { username: username },
                });
                // If we processed pending auth, reload to ensure Auth picks up the tokens
                if (hasLoginCode) {
                    window.location.reload();
                }
            }
            catch (error) {
                logger.error("Error processing pending auth tokens:", error);
                storage.removeItem(pendingAuthKey);
            }
        }
    }
    return cognitoConfig;
});
let amplifyAuthPromise = null;
const initializeAmplifyAuth = (authConfig, domainName) => __awaiter(void 0, void 0, void 0, function* () {
    if (!amplifyAuthPromise) {
        amplifyAuthPromise = (() => __awaiter(void 0, void 0, void 0, function* () {
            try {
                yield initializeAuth(authConfig, domainName);
            }
            catch (err) {
                logger.error("Error initializing Amplify Auth", err);
                // reset the promise to allow for a new attempt
                amplifyAuthPromise = null;
            }
        }))();
    }
    return amplifyAuthPromise;
});
const getAccessToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield Auth.currentSession().catch((_e) => {
        return null;
    });
    if (!session) {
        return null;
    }
    const accessToken = session.getAccessToken();
    return accessToken;
});
const getIdToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield Auth.currentSession().catch((_e) => {
        return null;
    });
    if (!session) {
        return null;
    }
    const idToken = session.getIdToken();
    return idToken;
});
const getRefreshToken = () => __awaiter(void 0, void 0, void 0, function* () {
    const session = yield Auth.currentSession().catch((_e) => {
        return null;
    });
    if (!session) {
        return null;
    }
    return session === null || session === void 0 ? void 0 : session.getRefreshToken();
});
// Promise cache for deduplicating concurrent calls
let pendingUserSignedInStatePromise = null;
const createAuthResponse = (userInfo) => {
    if (!userInfo || !userInfo.username) {
        return { signedIn: false, userInfo: null };
    }
    return { signedIn: true, userInfo };
};
const getUserSignedInState = () => __awaiter(void 0, void 0, void 0, function* () {
    // Return existing promise if one is pending
    if (pendingUserSignedInStatePromise) {
        return pendingUserSignedInStatePromise;
    }
    // Create new promise and cache it
    pendingUserSignedInStatePromise = (() => __awaiter(void 0, void 0, void 0, function* () {
        try {
            const session = yield Auth.currentSession();
            if (!session || !session.isValid()) {
                return createAuthResponse(undefined);
            }
            const userInfo = yield Auth.currentUserInfo();
            if (!userInfo || !userInfo.username) {
                return createAuthResponse(undefined);
            }
            return createAuthResponse(userInfo);
        }
        catch (e) {
            return createAuthResponse(undefined);
        }
        finally {
            pendingUserSignedInStatePromise = null;
        }
    }))();
    return pendingUserSignedInStatePromise;
});
const signOut = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        yield waitForAuthConfigured();
        yield Auth.signOut();
        const jwt = yield getJwt();
        if (jwt) {
            logger.error("Failed to sign out, JWT still exists");
        }
        else {
            logger.debug("Signed out successfully dispatching signOut event and clearing query cache");
            AmplifyHub.dispatch("auth", { event: "signOut" });
        }
    }
    catch (e) {
        logger.error("Error signing out", e);
    }
});
const getJwt = () => __awaiter(void 0, void 0, void 0, function* () {
    try {
        const accessToken = yield getAccessToken();
        return (accessToken === null || accessToken === void 0 ? void 0 : accessToken.getJwtToken()) || "";
    }
    catch (e) {
        if (e !== "No current user") {
            logger.error("Error getting JWT", e);
        }
        return "";
    }
});
const getVisitorIdHeader = () => __awaiter(void 0, void 0, void 0, function* () {
    const uniqueVisitorId = yield tryGetVisitorId();
    const uniqueVisitorIdFromCookie = getVisitorIdCookie();
    const uniqueVisitorIdToUse = uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
        ? uniqueVisitorIdFromCookie
        : uniqueVisitorId;
    const headers = {};
    if (uniqueVisitorIdToUse) {
        headers["x-bc-visitor-id"] = uniqueVisitorIdToUse;
    }
    return headers;
});
const getAuthorizationHeader = () => __awaiter(void 0, void 0, void 0, function* () {
    const headers = {};
    const sessionToken = yield getAccessToken();
    if (sessionToken) {
        headers["Authorization"] = `Bearer ${sessionToken.getJwtToken()}`;
    }
    return headers;
});
const getHeadersWithUniqueVisitorId = () => __awaiter(void 0, void 0, void 0, function* () {
    const uniqueVisitorId = yield tryGetVisitorId();
    const uniqueVisitorIdFromCookie = getVisitorIdCookie();
    const uniqueVisitorIdToUse = uniqueVisitorIdFromCookie && uniqueVisitorIdFromCookie !== ""
        ? uniqueVisitorIdFromCookie
        : uniqueVisitorId;
    const jwt = yield getJwt();
    const headers = {
        "Content-Type": "application/json"
    };
    if (jwt) {
        headers["Authorization"] = `Bearer ${jwt}`;
    }
    if (uniqueVisitorIdToUse) {
        headers["x-bc-visitor-id"] = uniqueVisitorIdToUse;
    }
    return headers;
});
const setOtpAuth = (userInfo) => __awaiter(void 0, void 0, void 0, function* () {
    return dispatchSignInComplete(userInfo);
});
const dispatchSignInComplete = (userInfo) => {
    userInfo = Object.assign(Object.assign(Object.assign({}, userInfo), (userInfo.cognitoUserAttributes || {})), { username: userInfo.username || userInfo.userId });
    Hub.dispatch("auth", {
        event: "signIn_Complete",
        data: { username: userInfo.username },
    });
};
const getVisitorIdCookie = () => {
    const name = `${sharedVisitorIdCookieName}=`;
    const decodedCookie = decodeURIComponent(document.cookie);
    const ca = decodedCookie.split(";");
    for (let i = 0; i < ca.length; i++) {
        let c = ca[i];
        while (c.charAt(0) === " ") {
            c = c.substring(1);
        }
        if (c.indexOf(name) === 0) {
            return c.substring(name.length, c.length);
        }
    }
    return "";
};

export { AmplifyAuth, AmplifyHub, dispatchSignInComplete, getAccessToken, getAuthorizationHeader, getHeadersWithUniqueVisitorId, getIdToken, getJwt, getRefreshToken, getUserSignedInState, getVisitorIdCookie, getVisitorIdHeader, initializeAmplifyAuth, setOtpAuth, signOut, waitForAuthConfigured };
//# sourceMappingURL=index.js.map
